/* eslint-disable react/jsx-props-no-spreading */
import { CircularProgress, Divider, Stack, Typography } from '@mui/material';
import {
    FormControlProvider,
    FormStepComponentProps,
    QuestionnaireFormData,
    QuestionnaireResponseForm,
    SIMDocumentTypeAttachmentOption,
} from 'common';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { PostSignupFormData, PostSignupFormSchema } from '../schemas';
import { SimAttachmentsDropzone } from '../SimAttachmentsDropzone';
import { LockedFormNotice, getLockedFormStatus } from '../LockedFormNotice';
import { SubmissionState } from '../ReviewAndSubmit';
import { createEmptyQuestionnaireAnswers } from './questionnaireSimSchema';

type QuestionnaireSimStepProps = FormStepComponentProps<typeof PostSignupFormSchema> & {
    questionnaires: QuestionnaireFormData[];
    isLoadingQuestionnaires: boolean;
    attachmentOptionsBySubTypeId: Record<number, SIMDocumentTypeAttachmentOption>;
    /** Keyed by submission doc key (`questionnaire:<subTypeId>`). */
    attachments: Record<string, File[]>;
    attachmentErrors: Record<string, boolean>;
    onAttachmentsChange: (key: string, files: File[]) => void;
    submissionStates: Record<string, SubmissionState>;
};

export function QuestionnaireSimStep({
    control,
    questionnaires,
    isLoadingQuestionnaires,
    attachmentOptionsBySubTypeId,
    attachments,
    attachmentErrors,
    onAttachmentsChange,
    submissionStates,
}: QuestionnaireSimStepProps) {
    const { setValue, getValues } = useFormContext<PostSignupFormData>();

    useEffect(() => {
        if (questionnaires.length === 0) {
            return;
        }

        const currentEntries = getValues('questionnaires');
        if (currentEntries.length > 0) {
            return;
        }

        setValue(
            'questionnaires',
            questionnaires.map((questionnaire) =>
                createEmptyQuestionnaireAnswers(questionnaire.businessDocTypeViewModel.BusinessDocSubTypeId!)
            ),
            { shouldValidate: false }
        );
    }, [getValues, questionnaires, setValue]);

    if (isLoadingQuestionnaires) {
        return (
            <Stack direction="row" justifyContent="center" alignItems="center" p={4}>
                <CircularProgress size={28} />
            </Stack>
        );
    }

    if (questionnaires.length === 0) {
        return null;
    }

    return (
        <Stack spacing={3} p={{ xs: 2, sm: 0 }}>
            <Typography>
                Your customer has requested the following questionnaires. Required questions are marked.
            </Typography>
            {questionnaires.map((questionnaire, questionnaireIndex) => {
                const businessDocSubTypeId = questionnaire.businessDocTypeViewModel.BusinessDocSubTypeId;

                if (businessDocSubTypeId == null) {
                    return null;
                }

                const docKey = `questionnaire:${businessDocSubTypeId}`;
                const lockedStatus = getLockedFormStatus(submissionStates[docKey]);
                const handleFilesChange = (files: File[]) => onAttachmentsChange(docKey, files);

                return (
                    <Stack key={businessDocSubTypeId} spacing={3}>
                        {questionnaireIndex > 0 ? <Divider /> : null}
                        <LockedFormNotice status={lockedStatus} />
                        <FormControlProvider value={{ disabled: lockedStatus != null }}>
                            <QuestionnaireResponseForm
                                questionnaire={questionnaire}
                                control={control}
                                namePrefix={`questionnaires.${questionnaireIndex}` as `questionnaires.${number}`}
                            />
                            <SimAttachmentsDropzone
                                attachmentOption={
                                    attachmentOptionsBySubTypeId[businessDocSubTypeId] ??
                                    SIMDocumentTypeAttachmentOption.NoAttachments
                                }
                                files={attachments[docKey] ?? []}
                                onFilesChange={handleFilesChange}
                                hasError={attachmentErrors[docKey] ?? false}
                                testId={`questionnaire-${businessDocSubTypeId}-attachments`}
                            />
                        </FormControlProvider>
                    </Stack>
                );
            })}
        </Stack>
    );
}
